/**
 * Version history panel for LeadDetailPage (Round 2 f1-version-history).
 *
 * Lists every version of a lead's design (metadata only — versionId/
 * authorKind/createdAt/label, from lib/designVersions.ts's
 * getVersionMetadata()), newest-first (matching this repo's other lists —
 * LeadsPage's table and LeadsByTimeIndex query are both newest-first via
 * ScanIndexForward: false). `versions[0]` in STORAGE order (the oldest, the
 * original homeowner submission) is marked with an "Original submission"
 * badge regardless of where it lands in the reversed display order.
 *
 * No thumbnails: this repo has no existing lightweight thumbnail/snapshot
 * mechanism (no @dockpro/ui dependency, no client-side render-to-image
 * utility), and the embed itself is the only thing that can render a dock
 * design — reusing it here would mean N extra hidden iframes per lead just
 * to produce a thumbnail. A text/metadata list (author kind, timestamp,
 * label) is the honest, lightweight choice instead — see DEBT.md.
 *
 * Clicking a row's "View" loads that version's full designState into the
 * SAME read-only, one-way `load-design` postMessage mechanism LeadDetailPage
 * already uses to preview the lead's current design (see embedMessaging.ts)
 * — this component itself never touches postMessage; it only reports which
 * version was picked via onView, and the page wires that into the existing
 * preview pipeline.
 *
 * `disabled` (Round 4 — live-edit capstone): while the page's iframe is in
 * live-edit mode, switching to view/restore an old version would silently
 * swap the SAME iframe's content out from under the builder's in-progress
 * (unsaved) edits — the same "never silently overwrite" concern the save
 * flow itself guards against. Rather than reasoning about that interaction
 * (e.g. an old-version preview mid-edit, or a restore's lead-refresh
 * re-triggering the edit iframe's own load), this panel is simply disabled
 * outright during edit mode — the page shows a one-line explanation next
 * to it. A deliberate scope-narrowing judgment call, not an oversight.
 */
import type { DesignVersionMetadata } from "../lib/api";
import { StatusMessage } from "./StatusMessage";
import ui from "../styles/ui.module.css";
import styles from "./VersionHistoryPanel.module.css";

interface Props {
  versions: DesignVersionMetadata[]; // storage order (oldest-first); versions[0] is the original
  currentVersionId?: string;
  viewingVersionId: string | null; // null = previewing the lead's current design
  pendingViewVersionId: string | null; // versionId currently being fetched for preview
  onView: (versionId: string) => void;
  onViewCurrent: () => void;
  restoringVersionId: string | null; // versionId currently being restored, if any
  onRestore: (versionId: string) => void;
  // Surfaces either a "view an old version" fetch failure or a restore
  // failure (including the 409 stale-version copy) — both are this panel's
  // own concern, shown in one slot rather than two.
  error: string | null;
  // Round 4 — live-edit capstone. See the file header note above.
  disabled?: boolean;
}

export function VersionHistoryPanel({
  versions, currentVersionId, viewingVersionId, pendingViewVersionId,
  onView, onViewCurrent, restoringVersionId, onRestore, error,
  disabled = false,
}: Props) {
  const originalVersionId = versions[0]?.versionId;
  const newestFirst = [...versions].reverse();

  return (
    <section className={ui.card}>
      {/* Round 6 polish audit (AUD-016): promoted h3->h2 so this doesn't skip
          a level under LeadDetailPage's own h1 (the homeowner's name). */}
      <h2 className={ui.cardTitle}>Version history</h2>

      {disabled && (
        <p className={ui.subtle}>
          Version history is unavailable while editing the design. Save or cancel your edits to browse other versions.
        </p>
      )}

      {versions.length === 0 ? (
        <p className={ui.subtle}>
          No version history for this lead yet.
        </p>
      ) : (
        <ul className={styles.list}>
          {newestFirst.map((version) => {
            const isCurrent = version.versionId === currentVersionId;
            const isOriginal = version.versionId === originalVersionId;
            const isViewing = version.versionId === viewingVersionId;
            const isLoadingView = pendingViewVersionId === version.versionId;
            const isRestoring = restoringVersionId === version.versionId;

            return (
              <li
                key={version.versionId}
                className={`${styles.row} ${isViewing ? styles.rowActive : ""}`}
              >
                <div className={styles.rowMain}>
                  <div className={ui.badges}>
                    <span className={`${ui.badge} ${
                      version.authorKind === "homeowner" ? ui.badgeInfo : ui.badgeBuilder
                    }`}
                    >
                      {version.authorKind === "homeowner" ? "Homeowner" : "Builder"}
                    </span>
                    {isOriginal && (
                      <span className={`${ui.badge} ${ui.badgeTan}`}>
                        Original submission
                      </span>
                    )}
                    {isCurrent && (
                      <span className={`${ui.badge} ${ui.badgeOk}`}>
                        Current
                      </span>
                    )}
                  </div>
                  <div className={styles.rowMeta}>
                    <span className={styles.timestamp}>{formatVersionTimestamp(version.createdAt)}</span>
                    {version.label && <span className={styles.label}>{version.label}</span>}
                  </div>
                </div>

                <div className={styles.rowActions}>
                  <button
                    type="button"
                    className={`${ui.buttonSecondary} ${ui.buttonSmall}`}
                    onClick={() => onView(version.versionId)}
                    disabled={disabled || isLoadingView}
                  >
                    {isLoadingView ? "Loading…" : isViewing ? "Viewing" : "View"}
                  </button>
                  {!isCurrent && (
                    <button
                      type="button"
                      className={`${ui.button} ${ui.buttonSmall}`}
                      onClick={() => onRestore(version.versionId)}
                      disabled={disabled || restoringVersionId !== null}
                    >
                      {isRestoring ? "Restoring…" : "Restore this version"}
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {viewingVersionId !== null && (
        <button type="button" className={`${ui.buttonSecondary} ${ui.buttonSmall} ${styles.viewCurrentButton}`} onClick={onViewCurrent}>
          ← Back to current design
        </button>
      )}

      {error && (
        <StatusMessage variant="error" message={error} className={`${ui.error} ${styles.errorMessage}`} />
      )}
    </section>
  );
}

function formatVersionTimestamp(createdAt: string | null): string {
  if (!createdAt) return "Unknown date";
  const d = new Date(createdAt);
  if (Number.isNaN(d.getTime())) return "Unknown date";
  return d.toLocaleString("en-US", { dateStyle: "long", timeStyle: "short" });
}
